import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '@/services/api/projects';
import type { Database } from '@/types/database.types';

type ProjectInsert = Database['public']['Tables']['projects']['Insert'];
type ProjectUpdate = Database['public']['Tables']['projects']['Update'];

/**
 * Hook to fetch all projects for the current user
 */
export const useProjects = () => {
    return useQuery({
        queryKey: ['projects'],
        queryFn: async () => {
            const { data, error } = await projectsApi.getAll();
            if (error) throw error;
            return data ?? [];
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to fetch a single project by ID
 */
export const useProject = (id: number) => {
    return useQuery({
        queryKey: ['projects', id],
        queryFn: async () => {
            const { data, error } = await projectsApi.getById(id);
            if (error) throw error;
            return data;
        },
        enabled: !!id,
    });
};

/**
 * Hook to create a new project
 */
export const useCreateProject = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (project: ProjectInsert) => {
            const { data, error } = await projectsApi.create(project);
            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            // Invalidate and refetch projects list
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
};

/**
 * Hook to update an existing project
 */
export const useUpdateProject = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, updates }: { id: number; updates: ProjectUpdate }) => {
            const { data, error } = await projectsApi.update(id, updates);
            if (error) throw error;
            return data;
        },
        onSuccess: (data) => {
            // Invalidate both the list and the specific project
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            if (data?.id) {
                queryClient.invalidateQueries({ queryKey: ['projects', data.id] });
            }
        },
    });
};

/**
 * Hook to delete a project
 */
export const useDeleteProject = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            const { error } = await projectsApi.delete(id);
            if (error) throw error;
        },
        onSuccess: () => {
            // Invalidate projects list
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });
};

